import { create } from 'zustand';

/**
 * AutosaveRegistry
 *
 * A global registry to track in-flight autosave operations across the application.
 * This prevents parallel saves for the same resource (e.g., a specific task)
 * even if the component using the hook unmounts and remounts.
 */

interface AutosaveRegistryState {
  /** Map of lock keys to their in-flight status */
  inFlightSaves: Record<string, boolean>;

  // Actions
  /** Set the in-flight status for a specific key */
  setInFlight: (key: string, isInFlight: boolean) => void;
  /** Check if a specific key is currently saving */
  isSaving: (key: string | undefined) => boolean;
}

export const useAutosaveRegistryStore = create<AutosaveRegistryState>((set, get) => ({
  inFlightSaves: {},

  setInFlight: (key, isInFlight) => {
    set((state) => ({
      inFlightSaves: {
        ...state.inFlightSaves,
        [key]: isInFlight,
      },
    }));
  },

  isSaving: (key) => {
    if (!key) return false;
    return !!get().inFlightSaves[key];
  },
}));

/**
 * Specialized hook for accessing autosave registry actions and state
 */
export const useAutosaveRegistry = () => {
  const setInFlight = useAutosaveRegistryStore((state) => state.setInFlight);
  const isSaving = useAutosaveRegistryStore((state) => state.isSaving);

  return {
    setInFlight,
    isSaving,
  };
};
